import { useEffect, useRef, useState } from "react";
import { CheckCircle, Clock, Coins, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PixPaymentDialog } from "@/components/checkout/PixPaymentDialog";
import {
  CreditCardForm,
  isCardDataComplete,
  toCheckoutCreditCard,
  type CardFormState,
} from "@/components/checkout/CreditCardForm";
import {
  CardHolderForm,
  EMPTY_CARD_HOLDER,
  isCardHolderComplete,
  isCardHolderPhoneValid,
  toCreditCardHolder,
  type CardHolderFormState,
} from "@/components/checkout/CardHolderForm";
import { PaymentMethodSelector } from "@/components/checkout/PaymentMethodSelector";
import {
  fetchAiCreditPurchaseQr,
  listAiCreditPacks,
  purchaseAiCreditPack,
} from "@/services/ai-credit-packs.service";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type {
  AiCreditPackOffer,
  CheckoutPaymentResponse,
} from "@/backend-sdk/client/types.gen";

/**
 * <M-280> Comprar um pacote de créditos de IA para uma loja.
 *
 * O fluxo de pagamento é o do upgrade de plano (`UpgradeDialog`): PIX com QR na tela e
 * confirmação por polling, ou cartão com o titular. Os créditos caem no saldo comprado da loja
 * quando o pagamento é confirmado, e não expiram — o chat e a auditoria só chegam neles depois
 * de a franquia do mês acabar.
 */

const EMPTY_CARD: CardFormState = {
  holderName: "",
  number: "",
  expiryMonth: "",
  expiryYear: "",
  ccv: "",
};

// O QR do PIX pode demorar para o gateway gerar: consulta a cada 2s, até 15 vezes.
const PIX_QR_POLL_INTERVAL_MS = 2000;
const PIX_QR_MAX_ATTEMPTS = 15;

const formatBRL = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);

const formatBRLPorCredito = (value: number) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(value);

interface BuyAiCreditsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A loja que recebe os créditos — `accounts.id`. */
  customerUuid: string;
  storeName?: string | null;
  /** Chamado ao fechar depois de uma compra confirmada, para quem abriu recarregar o saldo. */
  onPurchased?: () => void;
}

export const BuyAiCreditsDialog = ({
  open,
  onOpenChange,
  customerUuid,
  storeName,
  onPurchased,
}: BuyAiCreditsDialogProps) => {
  const [packs, setPacks] = useState<AiCreditPackOffer[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<"pix" | "credit_card">(
    "pix",
  );
  const [cardData, setCardData] = useState<CardFormState>(EMPTY_CARD);
  const [cardHolder, setCardHolder] =
    useState<CardHolderFormState>(EMPTY_CARD_HOLDER);
  const [submitting, setSubmitting] = useState(false);
  const [checkout, setCheckout] = useState<CheckoutPaymentResponse | null>(
    null,
  );
  const [pixOpen, setPixOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [success, setSuccess] = useState(false);
  // Cada tentativa de compra ganha um número; fechar o diálogo ou comprar de novo o avança, e o
  // polling do QR de uma tentativa anterior se descobre obsoleto em vez de escrever o QR dela
  // na cobrança nova.
  const tentativaRef = useRef(0);

  useEffect(() => {
    if (!open) return;
    let ativo = true;
    listAiCreditPacks()
      .then((lista) => {
        if (!ativo) return;
        setLoadError(false);
        setPacks(lista);
        setSelectedId((atual) => atual ?? lista[0]?.id ?? null);
      })
      .catch(() => {
        if (ativo) setLoadError(true);
      });
    return () => {
      ativo = false;
    };
  }, [open]);

  const reset = () => {
    tentativaRef.current += 1;
    setLoadError(false);
    setSelectedId(null);
    setPaymentMethod("pix");
    setCardData(EMPTY_CARD);
    setCardHolder(EMPTY_CARD_HOLDER);
    setSubmitting(false);
    setCheckout(null);
    setPixOpen(false);
    setPending(false);
    setSuccess(false);
  };

  const close = () => {
    const wasSuccess = success;
    reset();
    onOpenChange(false);
    if (wasSuccess) onPurchased?.();
  };

  const waitForPixQr = async (invoiceId: string, tentativa: number) => {
    const obsoleta = () => tentativa !== tentativaRef.current;
    for (let attempt = 0; attempt < PIX_QR_MAX_ATTEMPTS; attempt++) {
      if (obsoleta()) return;
      await new Promise((resolve) =>
        setTimeout(resolve, PIX_QR_POLL_INTERVAL_MS),
      );
      if (obsoleta()) return;
      try {
        const data = await fetchAiCreditPurchaseQr(invoiceId);
        if (obsoleta()) return;
        if (data.pix_copy_paste) {
          setCheckout((prev) =>
            prev ? { ...prev, pix_copy_paste: data.pix_copy_paste } : prev,
          );
          setPixOpen(true);
          return;
        }
      } catch {
        // QR ainda indisponível, ou erro transitório: continua tentando.
      }
    }
    if (!obsoleta()) setPending(true);
  };

  const selected = packs?.find((pack) => pack.id === selectedId) ?? null;

  const handleConfirm = async () => {
    if (!selected || submitting) return;
    if (paymentMethod === "credit_card" && !isCardDataComplete(cardData)) {
      toast({
        title: "Dados do cartão incompletos",
        description: "Preencha todos os campos do cartão de crédito",
        variant: "destructive",
      });
      return;
    }
    if (paymentMethod === "credit_card" && !isCardHolderComplete(cardHolder)) {
      toast({
        title: "Endereço do titular incompleto",
        description: "Informe o CEP e o número do endereço do titular do cartão",
        variant: "destructive",
      });
      return;
    }
    if (paymentMethod === "credit_card" && !isCardHolderPhoneValid(cardHolder)) {
      toast({
        title: "Telefone do titular inválido",
        description:
          "Informe o telefone com DDD, ou deixe em branco para usar o do seu cadastro",
        variant: "destructive",
      });
      return;
    }

    const tentativa = ++tentativaRef.current;
    const obsoleta = () => tentativa !== tentativaRef.current;
    setSubmitting(true);
    try {
      const res = await purchaseAiCreditPack({
        pack_id: selected.id,
        customer_uuid: customerUuid,
        payment_method: paymentMethod,
        credit_card:
          paymentMethod === "credit_card"
            ? toCheckoutCreditCard(cardData)
            : undefined,
        credit_card_holder:
          paymentMethod === "credit_card"
            ? toCreditCardHolder(cardHolder)
            : undefined,
      });
      if (obsoleta()) return;
      setCheckout(res);

      if (res.status === "CONFIRMED" || res.status === "RECEIVED") {
        setSuccess(true);
      } else if (res.pix_copy_paste) {
        setPixOpen(true);
      } else if (res.billing_type === "PIX" && res.invoice_id) {
        await waitForPixQr(res.invoice_id, tentativa);
      } else {
        setPending(true);
      }
    } catch (error: unknown) {
      if (obsoleta()) return;
      const message =
        error &&
        typeof error === "object" &&
        "message" in error &&
        typeof error.message === "string"
          ? error.message
          : "Não foi possível concluir a compra. Tente novamente.";
      toast({
        title: "Erro na compra",
        description: message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const destino = storeName ? (
    <>
      para a loja <strong>{storeName}</strong>
    </>
  ) : (
    "para esta loja"
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      <DialogContent className="sm:max-w-md">
        {success ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                Compra confirmada!
              </DialogTitle>
              <DialogDescription>
                {selected
                  ? `${selected.credits.toLocaleString("pt-BR")} créditos de IA foram adicionados ao saldo. Eles não expiram.`
                  : "Os créditos foram adicionados ao saldo e não expiram."}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close}>Fechar</Button>
            </DialogFooter>
          </>
        ) : pending ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-500" />
                Pagamento em análise
              </DialogTitle>
              <DialogDescription>
                Os créditos entram no saldo assim que o pagamento for
                confirmado. Isso pode levar alguns minutos.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button onClick={close}>Fechar</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-primary" />
                Comprar créditos de IA
              </DialogTitle>
              <DialogDescription>
                Créditos avulsos {destino}. Eles não expiram e são usados depois
                que a franquia do mês acabar.
              </DialogDescription>
            </DialogHeader>

            {loadError ? (
              <p className="text-sm text-destructive">
                Não foi possível carregar os pacotes. Tente novamente mais
                tarde.
              </p>
            ) : packs === null ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Carregando pacotes…
              </div>
            ) : packs.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nenhum pacote de créditos está à venda no momento.
              </p>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2" role="radiogroup" aria-label="Pacote">
                  {packs.map((pack) => {
                    const ativo = pack.id === selectedId;
                    return (
                      <button
                        key={pack.id}
                        type="button"
                        role="radio"
                        aria-checked={ativo}
                        onClick={() => setSelectedId(pack.id)}
                        className={cn(
                          "w-full flex items-center justify-between rounded-lg border p-3 text-left transition-colors",
                          ativo
                            ? "border-primary bg-primary/5"
                            : "hover:bg-muted/50",
                        )}
                      >
                        <span>
                          <span className="block font-medium">{pack.name}</span>
                          <span className="block text-sm text-muted-foreground">
                            {pack.credits.toLocaleString("pt-BR")} créditos ·{" "}
                            {formatBRLPorCredito(pack.unit_price)} por crédito
                          </span>
                        </span>
                        <span className="font-semibold">
                          {formatBRL(pack.price)}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="space-y-2">
                  <Label>Forma de pagamento</Label>
                  <PaymentMethodSelector
                    value={paymentMethod}
                    onChange={setPaymentMethod}
                  />
                </div>

                {paymentMethod === "credit_card" && (
                  <>
                    <CreditCardForm value={cardData} onChange={setCardData} />
                    <CardHolderForm
                      value={cardHolder}
                      onChange={setCardHolder}
                    />
                  </>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={submitting}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={submitting || !selected}
              >
                {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {submitting
                  ? "Processando..."
                  : selected
                    ? `Pagar ${formatBRL(selected.price)}`
                    : "Comprar"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>

      {checkout && (
        <PixPaymentDialog
          open={pixOpen}
          onOpenChange={setPixOpen}
          copyPaste={checkout.pix_copy_paste}
          dueDate={checkout.due_date}
          gatewayPaymentId={checkout.gateway_payment_id}
          description="Pague o PIX para adicionar os créditos ao saldo."
          onPaid={() => {
            setPixOpen(false);
            setSuccess(true);
          }}
        />
      )}
    </Dialog>
  );
};
